class Worldmap {

    /**
     * Constructor
     */
    constructor(mapElement) {
        this.namespace = 'worldmap';
        this.mapElement = mapElement;


        if (!this.mapElement) {
            return;
        }

        this.id = this.mapElement.id;
        this.data = null;
        this.svgElement = this.mapElement.querySelector('svg') || (this.mapElement.matches('svg') ? this.mapElement : null);
        this.dataElement = this.mapElement.querySelector('[data-' + this.namespace + '-data]');
        this.dataEndpoint = this.mapElement.getAttribute('data-' + this.namespace + '-dataendpoint');
        this.legendElement = document.querySelector(this.id ? '[data-worldmap-legend="' + this.id + '"]' : '[data-worldmap-legend]');

        if (this.legendElement) {
            this.legendElementItems = Array.from(this.legendElement.children);
        }
        this.pins = [];

        this.getData();

    }

    /**
     * Options
     * @return {Object}
     */
    get options() {
        return {
            pinRadius: 4,
        }
    }


    /**
     * Register events
     * Registers events on the pins and on the legend items (if the legend
     * exists), so that when hovering one, the corresponding item from the other is receiving a hover class
     * So, for example, when hovering a pin from the map, the corresponding legend item is also highlighted. And the other way around.
     */
    registerEvents() {
        this.svgElement.addEventListener('mouseover', delegate('.' + this.namespace + '-pin', (e) => this.activateHighlightedItem(this.legendElement, e.delegateTarget)));
        this.svgElement.addEventListener('mouseout', delegate('.' + this.namespace + '-pin', (e) => this.deactivateHighlightedItems(this.legendElement)));

        if (!this.legendElement) {
            return;
        }
        this.legendElement.addEventListener('mouseover', delegate('.' + this.namespace + '-legend-item', (e) => this.activateHighlightedItem(this.svgElement, e.delegateTarget)));
        this.legendElement.addEventListener('mouseout', delegate('.' + this.namespace + '-legend-item', (e) => this.deactivateHighlightedItems(this.svgElement)));
    }

    /**
     * Deactivate all items
     * @param {Element} itemCollection - the parent of the items that must be
     * deactivated. The parent can be the legend or the svgElement of the map.
     */
    deactivateHighlightedItems(itemCollection) {
        if (!itemCollection) return;

        let collectionCssClass = itemCollection.matches('[data-' + this.namespace + '-legend]') ? this.namespace + '-legend--hover' : this.namespace + '--hover';
        let cssClass = itemCollection.matches('[data-' + this.namespace + '-legend]') ? this.namespace + '-legend-item--hover' : this.namespace + '-pin--hover';

        Array.from(itemCollection.children).forEach((item) => item.classList.remove(cssClass));
        itemCollection.classList.remove(collectionCssClass);
    }

    /**
     * Activate item
     * @param {Element} itemCollection - the parent of the items that must be
     * activated. The parent can be the legend or the svgElement of the map.
     * @param {Element} correspondentItem - the correspondent item from the
     * other collection.
     */
    activateHighlightedItem(itemCollection, correspondentItem) {
        if (!itemCollection) return;

        let collectionCssClass = itemCollection.matches('[data-' + this.namespace + '-legend]') ? this.namespace + '-legend--hover' : this.namespace + '--hover';
        let cssClass = itemCollection.matches('[data-' + this.namespace + '-legend]') ? this.namespace + '-legend-item--hover' : this.namespace + '-pin--hover';
        let correspondentCollection = itemCollection.matches('[data-' + this.namespace + '-legend]') ? this.svgElement : this.legendElement;

        this.deactivateHighlightedItems(correspondentCollection);

        let item = itemCollection.querySelector('[data-coordinates="' + correspondentItem.getAttribute('data-coordinates') + '"][data-name="' + correspondentItem.getAttribute('data-name') + '"]');
        if (!item) return;

        item.classList.add(cssClass);
        itemCollection.classList.add(collectionCssClass);
    }


    /**
     * Get data
     */
    getData() {
        if (this.dataEndpoint) {
            // Get the countries to be added as pins on the map from the data endpoint
            fetch(this.dataEndpoint)
                .then((response) => {
                    if (!response.ok) {
                        console.error('Network response was not ok.');
                        return;
                    }

                    response.json().then((data) => {
                        this.data = data;
                        this.addPins();
                    });
                }
            );
            return;
        }

        if (this.dataElement) {
            this.data = JSON.parse(this.dataElement.textContent);
            this.addPins();
        }
    }



    /**
     * Add pins
     * Iterate through the this.data and add pins for its locations
     */
    addPins() {
        let locations = {};

        if (!this.data) return;

        this.data.forEach((location) => {
            let locationCountryPath = this.mapElement.querySelector('path[data-code="' + location.countryCode + '"]');
            let coordinates;
            let latitude;
            let longitude;
            let points;

            if (!location.coordinates && !locationCountryPath) return;

            coordinates = location.coordinates ? location.coordinates.split(',') : locationCountryPath.getAttribute('data-coordinates').split(',');
            latitude = parseFloat(coordinates[0]).toFixed(3);
            longitude = parseFloat(coordinates[1]).toFixed(3);

            points = Worldmap.getPointsFromCoordinates(latitude, longitude);

            if (locations[latitude+longitude]) {
                points.x = points.x + this.options.pinRadius;
            }
            locations[latitude+longitude] = true;

            this.pins.push(this.createPin(location, points));
        });

        this.registerEvents();

    }


    /**
     * Creates a pin at the designated points
     * @param String countryCode
     * @param Object points
     * @return Element Pin
     */
    createPin(location, points) {
        let pin = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        let pinCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        let pinBorderFirst = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        let pinBorderSecond = document.createElementNS('http://www.w3.org/2000/svg', 'circle');

        pin.classList.add(this.namespace + '-pin');

        pinCircle.setAttribute('r', this.options.pinRadius);
        pinCircle.setAttribute('cx', points.x);
        pinCircle.setAttribute('cy', points.y);

        pinBorderFirst.setAttribute('r', this.options.pinRadius * 1.2);
        pinBorderFirst.setAttribute('cx', points.x);
        pinBorderFirst.setAttribute('cy', points.y);

        pinBorderSecond.setAttribute('r', this.options.pinRadius * 1.8);
        pinBorderSecond.setAttribute('cx', points.x);
        pinBorderSecond.setAttribute('cy', points.y);

        pin.appendChild(pinBorderSecond);
        pin.appendChild(pinBorderFirst);
        pin.appendChild(pinCircle);
        pin.setAttribute('data-code', location.countryCode);
        pin.setAttribute('data-coordinates', location.coordinates);
        pin.setAttribute('data-name', location.name);

        if (location.attributes) {
            for (let key in location.attributes) {
                pin.setAttribute(key, location.attributes[key]);
            }
        }
        this.svgElement.appendChild(pin);

        return pin;
    }


    /**
     * Get points from lat/long coordinates (uses Miller projection)
     * @param Float latitude
     * @param Float longitude
     * @return Object points
     */
    static getPointsFromCoordinates(latitude, longitude) {
        let radiusToDegrees = Math.PI / 180;
        let earthRadius = 6381372;
        let centralMeridianDegrees = 11.5;

        let points;
        let mapWidth = 900;
        let mapHeight = 440;
        let mapBoundingBox = [
            {'x': -20004297.151525836, 'y': -12671671.123330014},
            {'x': 20026572.39474939, 'y': 6930392.025135122}
        ];

        if (longitude < (-180 + centralMeridianDegrees)) {
            longitude += 360;
        }

        // get points from coordinates using Miller projection
        points = {
            'x': earthRadius * (longitude - centralMeridianDegrees) * radiusToDegrees,
            'y': -earthRadius * Math.log(Math.tan((45 + 0.4 * latitude) * radiusToDegrees)) / 0.8
        };

        // adapt calculations using the boundingbox of the map and the map width/height
        points.x = (points.x - mapBoundingBox[0].x) / (mapBoundingBox[1].x - mapBoundingBox[0].x) * mapWidth;
        points.y = (points.y - mapBoundingBox[0].y) / (mapBoundingBox[1].y - mapBoundingBox[0].y) * mapHeight;

        return points;
    }

}


new Worldmap(document.querySelector('[data-worldmap]'));
